import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {useAgentResidualsImports, useUserInfo} from '@ozark/common';
import {Column} from '@ozark/common/api/Column';
import {BoxParentHeight, DATETIME_FORMAT, Loading, Table} from '@ozark/common/components';
import {useCallable} from '@ozark/common/hooks/useCallable';
import {arrayToCsv} from '@ozark/common/util';
import {AgentResidualsImportView} from '@ozark/functions/src/documents/Residual';
import {uniq} from '@s-libs/micro-dash';
import {format, parse} from 'date-fns';
import {useState} from 'react';
import {ActionButton} from './ActionButton';

type RowType = AgentResidualsImportView;

type ImportedFilesProps = {
  onDelete: () => void;
};

export const ImportedFiles = ({onDelete}: ImportedFilesProps) => {
  const [selected, setSelected] = useState<AgentResidualsImportView | null>(null);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const {documents: residualsImports} = useAgentResidualsImports();
  const {deleteImportedManualAdjustments} = useCallable();
  const [confirmationAction, setConfirmationAction] = useState<null | (() => void)>(null);
  const {isErpAdmin, isErpManager, isAgentAdmin} = useUserInfo();
  const handleRowClick = (row: AgentResidualsImportView) => {
    setSelected(row);
  };

  const handleDownloadClick = () => {
    if (!selected) return;
    const a = document.createElement('a');
    a.href = selected.fileUrl;
    a.setAttribute('download', `${selected.yearMonth}.csv`);
    a.click();
  };

  const exportToCsv = async () => {
    if (!selected?.importedRawAdjustmentData || !selected.importedRawAdjustmentData.length) {
      return;
    }
    const csvContent = arrayToCsv(selected.importedRawAdjustmentData);
    // Create a blob and trigger download
    const blob = new Blob([csvContent], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `residuals-${selected.yearMonth}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (): Promise<void> => {
    if (!selected) return;
    await deleteImportedManualAdjustments({importId: selected.ref.id});
    setSelected(null);
    setConfirmationAction(null);
    onDelete();
  };

  if (!isErpAdmin && !isErpManager && !isAgentAdmin) return null;

  if (residualsImports.promised) return <Loading />;

  if (!residualsImports.promised && !residualsImports.data) {
    return (
      <Box mt={6}>
        <Typography variant="body1" align="center">
          No imported files found.
        </Typography>
      </Box>
    );
  }
  const rows = residualsImports.data?.map((e, i) => ({...e, ...e.totals, id: i})) as
    | RowType[]
    | undefined;

  return (
    <>
      <BoxParentHeight overflow="unset" sx={{'& > div': {maxHeight: '100%'}}}>
        {rows && (
          <Table
            onRowClick={handleRowClick}
            rows={rows}
            selectedId={selected?.id}
            columns={getTableColumns()}
            actions={(row: RowType) => (
              <Box
                sx={{width: '85px', height: '20px'}}
                onClick={() => {
                  if (row.id !== selected?.id) {
                    setSelected(row);
                  }
                }}
              >
                {row.id === selected?.id && (
                  <ActionButton
                    selected={selected}
                    setShowErrors={setShowErrors}
                    readOnly={isErpManager}
                    handleDownloadClick={handleDownloadClick}
                    exportToCsv={exportToCsv}
                    handleDelete={async () =>
                      setConfirmationAction(() => async () => {
                        await handleDelete();
                        return Promise.resolve();
                      })
                    }
                  />
                )}
              </Box>
            )}
            scrollableBody
            stickyHeader
          />
        )}
      </BoxParentHeight>

      {confirmationAction && (
        <Dialog open onClose={() => setConfirmationAction(null)}>
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this residual import? This will delete the import file
            and all imported residual adjustments, it cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmationAction(null)} color="primary">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                if (confirmationAction) await confirmationAction();
              }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {selected?.warnings && selected.warnings.length > 0 && (
        <Dialog open={showErrors} maxWidth={'lg'}>
          <DialogTitle id="add-equipment-dialog-title">Import Errors</DialogTitle>
          <DialogContent>
            <List>
              {uniq(selected.warnings).map((warning: string, i: number) => {
                return (
                  <ListItem key={i}>
                    <ListItemText primary={<Typography gutterBottom>{warning}</Typography>} />
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={() => setShowErrors(false)} type="submit">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const formatCurrency = (value?: number) => {
  if (!value) return '';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return formatter.format(value as number);
};

const getTableColumns = (): Column<RowType>[] => [
  {
    id: 'yearMonth',
    label: 'File Date',
    selector: row => {
      const date = parse(row.yearMonth, 'yyyyMM', new Date());
      return format(date, 'MMMM, yyyy');
    },
  },
  {
    id: 'status',
    label: 'Import Status',
    width: 160,
    selector: row => row.status,
  },
  {
    id: 'count',
    label: 'Rows Imported',
    numeric: true,
    selector: row => row.totals?.items,
  },
  {
    id: 'net',
    label: 'Net',
    numeric: true,
    selector: row => formatCurrency(Number(row.totals?.net)),
  },
  {
    id: 'updatedAt',
    label: 'Last Updated',
    numeric: true,
    selector: row => {
      const date = row.updatedAt ?? row.createdAt;
      return date && format(date.toDate(), DATETIME_FORMAT);
    },
  },
  {
    id: 'createdAt',
    label: 'Created At',
    numeric: true,
    selector: row => {
      const date = row.updatedAt ?? row.createdAt;
      return date && format(date.toDate(), DATETIME_FORMAT);
    },
  },
];
